import { useRouter } from 'expo-router';
import {
  AlarmSmoke,
  CookingPot,
  Droplets,
  Fan,
  Flame,
  Refrigerator,
  ShowerHead,
  Soup,
  Utensils,
  WashingMachine,
  Wind,
  Wrench,
} from 'lucide-react-native';
import { ComponentType, useEffect } from 'react';

import { PrimaryCTA, Tile } from '@/components/onboarding/Controls';
import {
  Eyebrow,
  GridHint,
  H1,
  Screen,
  SelCount,
} from '@/components/onboarding/Screen';
import { TileGrid } from '@/components/onboarding/TileGrid';
import { Palette, Spacing } from '@/constants/theme';
import { qtyTotal, useOnboarding } from '@/store/onboarding';

const TILES: { k: string; label: string; Icon: ComponentType<any> }[] = [
  // Order roughly follows "what every home has" → "what some homes have."
  // The eight defaulted-on tiles appear first so the user sees that
  // we've pre-selected the obvious things; non-defaults sit below.
  { k: 'wh', label: 'Water heater', Icon: ShowerHead },
  { k: 'fridge', label: 'Refrigerator', Icon: Refrigerator },
  { k: 'range', label: 'Range / oven', Icon: CookingPot },
  { k: 'disp', label: 'Garbage disposal', Icon: Soup },
  { k: 'dw', label: 'Dishwasher', Icon: Utensils },
  { k: 'laundry', label: 'Washer & dryer', Icon: WashingMachine },
  { k: 'smoke', label: 'Smoke / CO alarms', Icon: AlarmSmoke },
  { k: 'bathfan', label: 'Bath exhaust fan', Icon: Fan },
  // Not-defaulted (opt in if you've got them).
  { k: 'soft', label: 'Water softener', Icon: Droplets },
  { k: 'sump', label: 'Sump pump', Icon: Droplets },
  { k: 'humid', label: 'Humidifier', Icon: Wind },
  { k: 'hood', label: 'Range hood', Icon: Wrench },
  { k: 'fire', label: 'Fireplace / chimney', Icon: Flame },
  { k: 'fans', label: 'Ceiling fans', Icon: Fan },
];

export default function InsideScreen() {
  const router = useRouter();
  const inside = useOnboarding((s) => s.inside);
  const toggleInside = useOnboarding((s) => s.toggleInside);
  const setInsideCount = useOnboarding((s) => s.setInsideCount);
  const applyInsideDefaults = useOnboarding((s) => s.applyInsideDefaults);

  // Pre-fill the common-everywhere set on first arrival. The store
  // guards against overwriting if the user has already touched the
  // map, so coming back via the back button is safe.
  useEffect(() => {
    applyInsideDefaults();
  }, [applyInsideDefaults]);

  return (
    <Screen
      scroll
      footer={
        <PrimaryCTA
          label="Continue"
          onPress={() => router.push('/(onboarding)/property' as never)}
        />
      }>
      <Eyebrow>6 · Inside</Eyebrow>
      <H1>What&apos;s inside?</H1>
      <GridHint>
        We&apos;ve pre-selected the things almost every home has. Tap to
        de-select anything you don&apos;t have, or add others. Use +/- when
        you have more than one (ceiling fans, fireplaces).
      </GridHint>

      <TileGrid>
        {TILES.map(({ k, label, Icon }) => {
          const count = inside[k] ?? 0;
          const selected = count > 0;
          return (
            <Tile
              key={k}
              label={label}
              selected={selected}
              count={count}
              icon={
                <Icon
                  size={18}
                  color={selected ? Palette.cream : Palette.forest}
                  strokeWidth={1.6}
                />
              }
              onPress={() => toggleInside(k)}
              onIncrement={() => setInsideCount(k, count + 1)}
              onDecrement={() => setInsideCount(k, count - 1)}
            />
          );
        })}
      </TileGrid>

      <SelCount count={qtyTotal(inside)} />

    </Screen>
  );
}
